import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateStateRequestSchema,
  type CreateStateRequest,
  type StateDetail,
  type StateListItem,
  UpdateStateRequestSchema,
  type UpdateStateRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useCountryOptions, useCreateState, useUpdateState } from '../use-states';

interface StateFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  state?: StateDetail | StateListItem | null;
}

const CREATE_DEFAULTS: CreateStateRequest = {
  countryId: '',
  name: '',
  isoCode: '',
  sortOrder: 0,
  isActive: true,
};

const StateFields = ({
  pending,
  countryOptions,
  countriesLoading,
}: {
  pending: boolean;
  countryOptions: ReadonlyArray<{ value: string; label: string }>;
  countriesLoading: boolean;
}): React.JSX.Element => (
  <>
    <UzFormField name="countryId">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Country"
          required
          placeholder={countriesLoading ? 'Loading countries…' : 'Select a country'}
          options={countryOptions}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          error={error}
          disabled={pending || countriesLoading}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Maharashtra"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isoCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="ISO 3166-2 code"
          placeholder="e.g. IN-MH"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const StateFormDrawer = ({
  open,
  onOpenChange,
  mode,
  state,
}: StateFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createState = useCreateState();
  const updateState = useUpdateState();
  const countriesQuery = useCountryOptions();
  const pending = createState.isPending || updateState.isPending;

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  const createForm = useForm<CreateStateRequest>({
    resolver: zodResolver(CreateStateRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateStateRequest>({
    resolver: zodResolver(UpdateStateRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (state) {
      updateForm.reset({
        countryId: state.country.id,
        name: state.name,
        isoCode: state.isoCode ?? '',
        sortOrder: state.sortOrder,
        isActive: state.isActive,
      });
    }
  }, [open, isCreate, state, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createState.mutateAsync({
      ...values,
      isoCode: values.isoCode?.trim() ? values.isoCode : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!state) return;
    await updateState.mutateAsync({
      id: state.id,
      payload: { ...values, isoCode: values.isoCode?.trim() ? values.isoCode : null },
    });
    onOpenChange(false);
  });

  const fields = (
    <StateFields
      pending={pending}
      countryOptions={countryOptions}
      countriesLoading={countriesQuery.isLoading}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add state' : `Edit ${state?.name ?? 'state'}`}
      description="A state belongs to a country and must be unique within it."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
